import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Tile } from "@carbon/react";
import { OfferResponse, ORDER_TYPES, OrderState } from "../../types";
import {
  calculateMinimumDecrement,
  LinePriceInput,
  LinePriceInputs,
  linePriceInputsOf,
  pricedLinesOf,
} from "../../utils";
import LinePriceTable from "../../components/LinePriceTable/LinePriceTable";
import ServiceAsked from "../../components/ServiceAsked/ServiceAsked";
import SellerOfferLine from "./SellerOfferLine";
import "./bid.scss";

export type JointSellerOfferLinesProps = {
  offers: OfferResponse[];
  isDeclined: boolean;
  editingOffers: Record<
    string,
    { price: string; currency: string; linePrices?: LinePriceInputs }
  >;
  updatingOfferId: string | null;
  errors: Record<string, string>;
  onInverseChange: (offer: OfferResponse, value: string) => void;
  onStartEdit: (offer: OfferResponse) => void;
  onEditPriceChange: (offerId: string, value: string) => void;
  onEditCurrencyChange: (offerId: string, value: string) => void;
  /** SCRUM-116: re-prices one line of a service quoted by line. */
  onEditLinePriceChange?: (
    offer: OfferResponse,
    code: string,
    patch: Partial<LinePriceInput>
  ) => void;
};

/**
 * The per-line list of a JOINT seller offer (Task 6) — the "split into … offer-table" piece.
 * Renders the shared column header then one `SellerOfferLine` per order line, wiring each line's
 * inverse-bid / normal-edit affordance to the parent's per-offer state maps. Mirrors the single
 * `SellerOffer.tsx` line but for every line in the group.
 */
const JointSellerOfferLines = ({
  offers,
  isDeclined,
  editingOffers,
  updatingOfferId,
  errors,
  onInverseChange,
  onStartEdit,
  onEditPriceChange,
  onEditCurrencyChange,
  onEditLinePriceChange,
}: JointSellerOfferLinesProps) => {
  const { t } = useTranslation();
  // SCRUM-116: services priced by line have no offer row, so no row header either.
  const anyOfferRow = offers?.some((o) => pricedLinesOf(o?.order).length === 0);
  const isInverseHeader =
    offers?.[0]?.order?.type === ORDER_TYPES.INVERSE_JOINT;

  return (
    <>
      {anyOfferRow && (
        <div className="seller-offer__columns">
          <span>{t("offer.columns.material")}</span>
          <span>{t("offer.columns.quantity")}</span>
          <span>
            {isInverseHeader
              ? t("offer.columns.yourBid")
              : t("offer.columns.yourPrice")}
          </span>
          <span>{t("offer.columns.total")}</span>
        </div>
      )}

      {offers?.map((offer) => {
        const isOrderOpen = offer?.order?.state === OrderState.OPEN;
        const isInverse =
          offer?.order?.type === ORDER_TYPES.INVERSE_JOINT && isOrderOpen;
        const isNormalJoint = offer?.order?.type === ORDER_TYPES.JOINT;
        const canEditNormalOffer =
          isNormalJoint && isOrderOpen && !offer?.selected && !isDeclined;

        const pricedLines = pricedLinesOf(offer?.order);
        const editing = editingOffers[offer?.id];

        return (
          <Fragment key={offer?.id}>
            {/* SCRUM-116: a service priced by line is offered in its line table, not on one row. */}
            {pricedLines.length === 0 && (
              <SellerOfferLine
                offer={offer}
                isInverse={isInverse}
                isDeclined={isDeclined}
                canEditNormalOffer={canEditNormalOffer}
                isEditing={!!editingOffers[offer?.id]}
                editPrice={editingOffers[offer?.id]?.price || ""}
                editCurrency={editingOffers[offer?.id]?.currency || ""}
                editDisabled={updatingOfferId === offer?.id}
                startEditDisabled={updatingOfferId !== null}
                inverseError={errors[offer?.id]}
                inversePlaceholder={calculateMinimumDecrement(
                  offer?.price,
                  offer?.order?.minimumDecrement
                )}
                onInverseChange={(value) => onInverseChange(offer, value)}
                onStartEdit={() => onStartEdit(offer)}
                onEditPriceChange={(value) =>
                  onEditPriceChange(offer?.id, value)
                }
                onEditCurrencyChange={(value) =>
                  onEditCurrencyChange(offer?.id, value)
                }
              />
            )}
            {pricedLines.length > 0 && (
              <Tile className="seller-offer__service-asked">
                <dl>
                  <ServiceAsked order={offer.order} />
                </dl>
              </Tile>
            )}
            {pricedLines.length > 0 && (
              <LinePriceTable
                onStartEdit={
                  canEditNormalOffer && updatingOfferId === null
                    ? () => onStartEdit(offer)
                    : undefined
                }
                lines={pricedLines}
                prices={
                  editing?.linePrices ??
                  linePriceInputsOf(
                    pricedLines,
                    offer?.linePrices,
                    offer?.currency
                  )
                }
                onPriceChange={
                  editing && canEditNormalOffer && onEditLinePriceChange
                    ? (code, unitPrice) =>
                        onEditLinePriceChange(offer, code, { unitPrice })
                    : undefined
                }
                onCurrencyChange={(code, currency) =>
                  onEditLinePriceChange?.(offer, code, { currency })
                }
                disabled={updatingOfferId === offer?.id}
                showMissing={!!editing}
                showDetail={false}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default JointSellerOfferLines;
